// components/GooeyCursor.jsx
import React, { useEffect, useRef } from "react";
import gsap from "gsap";

const GooeyCursor = () => {
  const cursorRef = useRef(null);
  const innerRef = useRef(null);
  const trailRefs = useRef([]);
  const hasMovedRef = useRef(false);

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    const isTouchDevice =
      "ontouchstart" in window || navigator.maxTouchPoints > 0;

    if (prefersReducedMotion || isTouchDevice) return;

    const cursor = cursorRef.current;
    const inner = innerRef.current;
    const trails = trailRefs.current.filter(Boolean);
    const blobs = [inner, ...trails].filter(Boolean);

    if (!cursor || !inner || trails.length === 0) return;

    gsap.set(blobs, {
      xPercent: -50,
      yPercent: -50,
      x: window.innerWidth / 2,
      y: window.innerHeight / 2,
      transformOrigin: "center center",
    });

    gsap.set(cursor, { autoAlpha: 0 });

    const moveCursor = (event) => {
      const { clientX: x, clientY: y } = event;

      if (!hasMovedRef.current) {
        hasMovedRef.current = true;

        gsap.to(cursor, {
          autoAlpha: 1,
          duration: 0.18,
          ease: "power2.out",
          overwrite: "auto",
        });
      }

      gsap.to(inner, {
        x,
        y,
        duration: 0.08,
        ease: "power3.out",
        overwrite: "auto",
      });

      trails.forEach((trail, index) => {
        gsap.to(trail, {
          x,
          y,
          duration: 0.18 + index * 0.1,
          ease: "power3.out",
          overwrite: "auto",
        });
      });
    };

    const shrink = () => {
      gsap.to(blobs, {
        scale: 0.64,
        duration: 0.16,
        ease: "power2.out",
        overwrite: "auto",
      });
    };

    const reset = () => {
      gsap.to(blobs, {
        scale: 1,
        duration: 0.35,
        ease: "elastic.out(1, 0.45)",
        overwrite: "auto",
      });
    };

    const grow = () => {
      gsap.to(inner, {
        scale: 1.85,
        duration: 0.22,
        ease: "power3.out",
        overwrite: "auto",
      });

      gsap.to(trails, {
        scale: 1.32,
        duration: 0.28,
        stagger: 0.015,
        ease: "power3.out",
        overwrite: "auto",
      });
    };

    const bindInteractiveElements = () => {
      const interactiveElements = document.querySelectorAll(
        "a, button, input, textarea, select, [role='button']"
      );

      interactiveElements.forEach((element) => {
        element.addEventListener("mouseenter", grow);
        element.addEventListener("mouseleave", reset);
      });

      return interactiveElements;
    };

    let interactiveElements = bindInteractiveElements();

    window.addEventListener("mousemove", moveCursor);
    window.addEventListener("mousedown", shrink);
    window.addEventListener("mouseup", reset);

    return () => {
      window.removeEventListener("mousemove", moveCursor);
      window.removeEventListener("mousedown", shrink);
      window.removeEventListener("mouseup", reset);

      interactiveElements.forEach((element) => {
        element.removeEventListener("mouseenter", grow);
        element.removeEventListener("mouseleave", reset);
      });
    };
  }, []);

  return (
    <>
      <svg
        className="pointer-events-none fixed h-0 w-0"
        aria-hidden="true"
        focusable="false"
      >
        <defs>
          <filter
            id="wwc-gooey-cursor-filter"
            x="-70%"
            y="-70%"
            width="240%"
            height="240%"
          >
            <feGaussianBlur
              in="SourceGraphic"
              stdDeviation="15"
              result="blur"
            />
            <feColorMatrix
              in="blur"
              mode="matrix"
              values="
                1 0 0 0 0
                0 1 0 0 0
                0 0 1 0 0
                0 0 0 34 -13
              "
              result="goo"
            />
            <feComposite in="SourceGraphic" in2="goo" operator="atop" />
          </filter>
        </defs>
      </svg>

      <div
        ref={cursorRef}
        className="pointer-events-none fixed inset-0 z-[999999] hidden md:block"
        style={{
          filter: "url(#wwc-gooey-cursor-filter)",
          WebkitFilter: "url(#wwc-gooey-cursor-filter)",
          mixBlendMode: "difference",
          opacity: 0,
        }}
      >
        {[0, 1, 2, 3, 4].map((item) => (
          <span
            key={item}
            ref={(node) => {
              trailRefs.current[item] = node;
            }}
            className="absolute left-0 top-0 rounded-full bg-white will-change-transform"
            style={{
              width: `${28 + item * 6}px`,
              height: `${28 + item * 6}px`,
              opacity: 0.24 + item * 0.11,
            }}
          />
        ))}

        <span
          ref={innerRef}
          className="absolute left-0 top-0 h-14 w-14 rounded-full bg-white opacity-100 will-change-transform"
        />
      </div>
    </>
  );
};

export default GooeyCursor;
